import React from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import PrivateRoute from '../PrivateRoute/PrivateRoute'
import ForgotPassword from '../ForgotPassword/ForgotPassword'
import ResetPassword from '../ResetPassword'
import NoWorkspaces from '../NoWorkspaces/NoWorkspaces'
import { ApolloProvider } from '@apollo/client/react'
import { ApolloClient, InMemoryCache, ApolloLink, HttpLink } from '@apollo/client'
import { SetContextLink } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { CombinedGraphQLErrors, CombinedProtocolErrors } from '@apollo/client/errors'
import { getLoginItem, getTenant, getBaseName, removeItem } from '../../utils'
import Home from '../Home/Home'
import Login from '../Login/Login'
import AppObjectRoute from '../AppObject/AppObjectRoute'
import CreateObject from '../AppObject/CreateObject'
import Statistics from '../Statistics/Statistics'
import CustomRoute from '../CustomRoute/CustomRoute'
import Attributes from '../Attributes/Attributes'
import Jobs from '../Jobs/Jobs'
import PDFFrames from '../PDFControls/PDFFrames'
import Search from '../Search/Search'
import SearchForm from '../SearchForm'
import SearchResults from '../SearchResults'
import NoNav from '../NoNav/NoNav'
import ObjectTypeSQL from '../ObjectTypeSQL/ObjectTypeSQL'
import moment from 'moment'
import { jwtDecode } from 'jwt-decode'
import './App.css'
import LoginExpired from '../Login/LoginExpired'
import CommonComponents from './CommonComponents'
import UserAttributes from '../UserAttributes/UserAttributes'
import RouteNotFound from './RouteNotFound'
import PoolStatistics from '../PoolStatistics/PoolStatitics'
import LogFileList from '../LogFileList/LogFileList'
import OpenFilesList from '../OpenFilesList/OpenFilesList'
import LogFile from '../LogFile/LogFile'
import ManageSchemas from '../ManageSchemas/ManageSchemas'
import ExportUserObjects from '../ObjectTypeSQL/ExportUserObjects'
import LoginAs from '../Login/LoginAs'
import UserProfile from '../UserProfile/UserProfile'
import ToolsRoute from '../Tools/ToolsRoute'
import SignUp from '../SignUp/SignUp'
import VerifyEmail from '../SignUp/VerifyEmail'

// a global list of reference list form inputs when submitting
// each array element is a child of the preceding element: [ {item1}, {childOfItem1}, {childOfItem}]
window.refListSubmitInputs = []
const tenant = getTenant()
const baseName = getBaseName()
const jwt = getLoginItem('jwt')

if (jwt) {
  const decoded = jwtDecode(jwt)
  const expDate = moment(decoded.exp * 1000)
  if (expDate.isBefore()) {
    removeItem(tenant)
  }
}

const httpLink = new HttpLink({
  uri: `${process.env.REACT_APP_SERVER}/graphql/${tenant}`,
})

const authLink = new SetContextLink((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = getLoginItem('jwt')

  // check for token expiration
  if (token) {
    const { exp } = jwtDecode(token)
    const expirationTime = exp * 1000
    //console.log('exp: ', exp, 'expirationTime: ', expirationTime, 'Date.now(): ', Date.now())
    if (Date.now() >= expirationTime) {
      const tenant = getTenant()
      window.location.href = `${window.location.origin}/${tenant}/loginExpired`
    }
  }

  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    }
  }
})

// Log any GraphQL errors, protocol errors, or network error that occurred
const errorLink = new ErrorLink(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path }) =>
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    )
  } else if (CombinedProtocolErrors.is(error)) {
    error.errors.forEach(({ message, extensions }) =>
      console.log(
        `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(
          extensions
        )}`
      )
    )
  } else {
    console.error(`[Network error]: ${error}`)
  }
})

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
}

const client = new ApolloClient({
  link: ApolloLink.from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: defaultOptions
})

// save a global reference to the ApolloClient
window.apolloClient = client

class App extends React.Component {
  componentDidMount() {
    if (tenant) {
      document.title = `CE2 - ${tenant.toUpperCase()}`
    }
  }

  render() {
    console.log('App rendered')
    return (
      < ApolloProvider client={client} > 
        <BrowserRouter basename={baseName}>
          <div className="app" data-cy="app">
            <Routes>
              <Route path="/signUp" element={<SignUp />} />
              <Route path="/verifyEmail" element={<VerifyEmail />} />
              <Route path="/login" element={<Login />} />
              <Route path="/loginAs" element={<LoginAs />} />
              <Route path="/forgotPassword" element={<ForgotPassword />} />
              <Route path="/resetPassword" element={<ResetPassword />} />
              <Route path="/noWorkspaces" element={<NoWorkspaces />} />
              <Route path="/loginExpired" element={<LoginExpired />} />
              <Route path="/pdf/:pdfname" element={<PrivateRoute path="/pdf" component={PDFFrames} />} />
              <Route path="/nonav/custom/:procedure" element={<PrivateRoute path="/nonav/custom/:procedure" component={NoNav} />} />
              <Route path="/" element={<><CommonComponents/><PrivateRoute path="/home" component={Home} /></>} />
              <Route path="/home" element={<><CommonComponents/><PrivateRoute path="/home" component={Home} /></>} />
              <Route path="/search/:id" element={<><CommonComponents/><PrivateRoute path="/search/:id" component={Search} /></>} />
              <Route path="/searchForm/:id" element={<><CommonComponents/><PrivateRoute path="/searchForm/:id" component={SearchForm} /></>} />
              <Route path="/searchResults/:id" element={<><CommonComponents/><PrivateRoute path="/searchResults/:id" component={SearchResults} /></>} />
              <Route path="/objectTypeSQL" element={<><CommonComponents/><PrivateRoute path="/objectTypeSQL" component={ObjectTypeSQL} /></>} />
              <Route path="/exportUserObjects" element={<><CommonComponents/><PrivateRoute path="/exportUserObjects" component={ExportUserObjects} /></>} />
              <Route path="/object/:id" element={<><CommonComponents/><PrivateRoute path="/object/:id" component={AppObjectRoute} /></>} />
              <Route path="/printView/:id" element={<PrivateRoute path="/printView/:id" component={AppObjectRoute} />} />
              <Route path="/create/:id" element={<><CommonComponents/><PrivateRoute path="/create/:id" component={CreateObject} /></>} />
              <Route path="/statistics" element={<><CommonComponents/><PrivateRoute path="/statistics" component={Statistics} /></>} />
              <Route path="/attributes" element={<><CommonComponents/><PrivateRoute path="/attributes" component={Attributes} /></>} />
              <Route path="/userAttributes" element={<><CommonComponents/><PrivateRoute path="/userAttributes" component={UserAttributes} /></>} />
              <Route path="/poolStatistics" element={<><CommonComponents/><PrivateRoute path="/poolStatistics" component={PoolStatistics} /></>} />
              <Route path="/logFileList" element={<><CommonComponents/><PrivateRoute path="/logFileList" component={LogFileList} /></>} />
              <Route path="/openFilesList" element={<><CommonComponents/><PrivateRoute path="/openFilesList" component={OpenFilesList } /></>} />
              <Route path="/logFile" element={<><CommonComponents/><PrivateRoute path="/logFile" component={LogFile} /></>} />
              <Route path="/manageSchemas" element={<><CommonComponents/><PrivateRoute path="/manageSchemas" component={ManageSchemas} /></>} />
              <Route path="/userProfile" element={<><CommonComponents/><PrivateRoute path="/userProfile" component={UserProfile} /></>} />
              <Route path="/jobs" element={<><CommonComponents/><PrivateRoute path="/jobs" component={Jobs} /></>} />
              <Route path="/custom/:procedure" element={<><CommonComponents/><PrivateRoute path="/custom/:procedure" component={CustomRoute} /></>} />
              <Route path="/tools/:procedure" element={<><CommonComponents/><PrivateRoute path="/tools/:procedure" component={ToolsRoute} /></>} />
              <Route path="*" element={<RouteNotFound />} />
            </Routes>
          </div>
        </BrowserRouter>
      </ApolloProvider >
    )
  }
}

export default App
